/**
 * FormLayoutSketch — szkicowy podgląd layoutu formularza z pliku importu (przed zapisem).
 *
 * Rysuje układ (zakładki, sekcje w siatce kolumn, pola jako puste pola z etykietą, bloki palety
 * jako opisane kafelki) i zaznacza NA MIEJSCU elementy, które nie wejdą (czerwone, przekreślone,
 * z powodem) albo wejdą z ostrzeżeniem (bursztynowe). To szkic układu, nie wierny render karty —
 * wierny widok daje kreator po imporcie (decyzja właścicielki 2026-10-21).
 *
 * Wejście: layout z pliku (pola po slugu, kształt `tree` albo `sections`) + wpisy raportu
 * z `elementId` + etykiety pól.
 */

import '@/components/modules/layout-elements';

import { createContext, type ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormImportReportEntry, FormLayoutShape } from '@saas/shared';
import { AlertTriangle, EyeOff, XCircle } from 'lucide-react';

import { Button } from '@/components/Button';
import { LayoutElementRegistry } from '@/components/fields/LayoutElementRegistry';
import { cn } from '@/lib/utils';

const P = 'tenant.formTransfer';

type Json = Record<string, unknown>;

function isObj(v: unknown): v is Json {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function objList(v: unknown): Json[] {
  return Array.isArray(v) ? v.filter(isObj) : [];
}

function columnsOf(el: Json, fallback: number): number {
  const s = isObj(el.settings) ? el.settings : el;
  const c = typeof s.columns === 'number' ? s.columns : fallback;
  return Math.min(Math.max(Math.round(c), 1), 3);
}

const GRID: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

interface SketchCtx {
  byId: ReadonlyMap<string, FormImportReportEntry>;
  fieldLabels: Record<string, string>;
}

const Ctx = createContext<SketchCtx>({ byId: new Map(), fieldLabels: {} });

function useReason() {
  const { t } = useTranslation();
  return (e: FormImportReportEntry | undefined) => (e?.reason ? t(`${P}.reasons.${e.reason}`) : '');
}

/** Ramka statusu wokół elementu: pominięty / z ostrzeżeniem / zwykły (bez ramki). */
function Mark({ id, children }: { id: unknown; children: ReactNode }) {
  const { byId } = useContext(Ctx);
  const reasonOf = useReason();
  const entry = typeof id === 'string' ? byId.get(id) : undefined;
  if (!entry || entry.status === 'loaded') return <>{children}</>;
  const skipped = entry.status === 'skipped';
  return (
    <div
      className={cn(
        'rounded-md border border-dashed p-1',
        skipped
          ? 'border-red-400 bg-red-50/60 dark:bg-red-900/10'
          : 'border-amber-400 bg-amber-50/60 dark:bg-amber-900/10',
      )}
      data-testid={`form-sketch-mark-${skipped ? 'skipped' : 'warning'}`}
    >
      <div className={cn(skipped && 'line-through opacity-60')}>{children}</div>
      <p
        className={cn(
          'mt-1 flex items-center gap-1 px-1 text-xs',
          skipped ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300',
        )}
      >
        {skipped ? (
          <XCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
        )}
        {reasonOf(entry)}
      </p>
    </div>
  );
}

function FieldBox({ el }: { el: Json }) {
  const { fieldLabels } = useContext(Ctx);
  const slug = str(el.fieldSlug);
  const label = str(isObj(el.settings) ? el.settings.label : '') || fieldLabels[slug] || slug;
  const hidden = el.hidden === true;
  return (
    <Mark id={el.id}>
      <div className={cn('space-y-1', hidden && 'opacity-50')} data-testid="form-sketch-field">
        <p className="flex items-center gap-1 text-xs font-medium text-text-secondary">
          {label}
          {hidden ? <EyeOff className="h-3 w-3" aria-hidden="true" /> : null}
        </p>
        <div className="h-8 rounded-md border border-border-secondary bg-bg-primary" />
      </div>
    </Mark>
  );
}

function BlockTile({ el }: { el: Json }) {
  const type = str(el.type);
  const def = LayoutElementRegistry.get(type);
  const settings = isObj(el.settings) ? el.settings : {};
  const title = str(settings.title) || str(settings.label) || str(settings.text);
  return (
    <Mark id={el.id}>
      <div className="rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2 text-xs text-text-secondary">
        <span className="font-medium text-text-primary">{def?.label ?? type}</span>
        {title ? <span> — {title}</span> : null}
      </div>
    </Mark>
  );
}

/** Pasek zakładek wspólny dla obu kształtów. */
function TabStrip({
  tabs,
  active,
  onSelect,
}: {
  tabs: Json[];
  active: number;
  onSelect: (i: number) => void;
}) {
  const { byId } = useContext(Ctx);
  return (
    <div className="flex flex-wrap gap-1 border-b border-border-secondary" role="tablist">
      {tabs.map((tab, i) => {
        const status = byId.get(str(tab.id))?.status;
        const hidden = tab.hidden === true;
        return (
          <Button
            key={str(tab.id) || i}
            variant="ghost"
            size="sm"
            role="tab"
            aria-selected={i === active}
            onClick={() => onSelect(i)}
            className={cn(
              'rounded-b-none border-b-2',
              i === active ? 'border-accent-600 text-accent-700' : 'border-transparent',
              status === 'skipped' && 'text-red-600 line-through',
              status === 'loaded_with_warning' && 'text-amber-700',
              hidden && 'opacity-50',
            )}
            leftIcon={hidden ? <EyeOff className="h-3 w-3" aria-hidden="true" /> : undefined}
            data-testid="form-sketch-tab"
          >
            {str(tab.title) || str(tab.slug)}
          </Button>
        );
      })}
    </div>
  );
}

function TreeTabs({ tabs }: { tabs: Json[] }) {
  const [active, setActive] = useState(0);
  const current = tabs[Math.min(active, tabs.length - 1)];
  return (
    <div className="space-y-3">
      <TabStrip tabs={tabs} active={active} onSelect={setActive} />
      {current ? (
        <Mark id={current.id}>
          <div className="grid gap-3">
            {objList(current.children).map((c, i) => (
              <TreeNode key={str(c.id) || i} el={c} />
            ))}
          </div>
        </Mark>
      ) : null}
    </div>
  );
}

function TreeNode({ el }: { el: Json }) {
  if (el.type === 'field') return <FieldBox el={el} />;
  if (Array.isArray(el.tabs)) return <TreeTabs tabs={objList(el.tabs)} />;
  const groups = Array.isArray(el.accordionItems)
    ? objList(el.accordionItems)
    : Array.isArray(el.steps)
      ? objList(el.steps)
      : null;
  if (!Array.isArray(el.children) && !groups) return <BlockTile el={el} />;

  const title = str(isObj(el.settings) ? el.settings.title : '');
  return (
    <Mark id={el.id}>
      <div className="rounded-lg border border-border-secondary p-3">
        {title ? <p className="mb-2 text-sm font-semibold text-text-primary">{title}</p> : null}
        {Array.isArray(el.children) ? (
          <div className={cn('grid gap-3', GRID[columnsOf(el, el.type === 'section' ? 2 : 1)])}>
            {objList(el.children).map((c, i) => (
              <TreeNode key={str(c.id) || i} el={c} />
            ))}
          </div>
        ) : null}
        {groups?.map((g, gi) => (
          <Mark id={g.id} key={str(g.id) || gi}>
            <div className="mt-2 rounded-md border border-border-primary p-2">
              <p className="mb-2 text-xs font-semibold text-text-primary">{str(g.title)}</p>
              <div className="grid gap-3">
                {objList(g.children).map((c, i) => (
                  <TreeNode key={str(c.id) || i} el={c} />
                ))}
              </div>
            </div>
          </Mark>
        ))}
      </div>
    </Mark>
  );
}

function SectionsSketch({ config }: { config: Json }) {
  const tabs = objList(config.tabs);
  const sections = objList(config.sections);
  const [active, setActive] = useState(0);
  const activeTab = tabs[Math.min(active, tabs.length - 1)];
  const tabIds = new Set(tabs.map((tb) => str(tb.id)));
  // Sekcja bez (znanej) zakładki należy do pierwszej — ta sama reguła co builder.
  const visible = activeTab
    ? sections.filter((s) => {
        const own = str(s.tabId);
        return tabIds.has(own) ? own === str(activeTab.id) : active === 0;
      })
    : sections;

  return (
    <div className="space-y-3">
      {tabs.length > 0 ? <TabStrip tabs={tabs} active={active} onSelect={setActive} /> : null}
      {visible.map((s, si) => (
        <Mark id={s.id} key={str(s.id) || si}>
          <div className="rounded-lg border border-border-secondary p-3">
            {str(s.title) ? (
              <p className="mb-2 text-sm font-semibold text-text-primary">{str(s.title)}</p>
            ) : null}
            <div className={cn('grid gap-3', GRID[columnsOf(s, 2)])}>
              {objList(s.elements).map((el, i) =>
                el.type === 'field' ? (
                  <FieldBox key={str(el.id) || i} el={el} />
                ) : (
                  <BlockTile key={str(el.id) || i} el={el} />
                ),
              )}
            </div>
          </div>
        </Mark>
      ))}
    </div>
  );
}

export interface FormLayoutSketchProps {
  shape: FormLayoutShape;
  config: Json;
  entries: FormImportReportEntry[];
  fieldLabels: Record<string, string>;
}

export function FormLayoutSketch({ shape, config, entries, fieldLabels }: FormLayoutSketchProps) {
  const { t } = useTranslation();
  const byId = new Map(entries.flatMap((e) => (e.elementId ? [[e.elementId, e] as const] : [])));

  return (
    <Ctx.Provider value={{ byId, fieldLabels }}>
      <div className="space-y-4" data-testid="form-layout-sketch">
        <div className="flex flex-wrap gap-4 text-xs text-text-secondary">
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm border border-dashed border-red-400 bg-red-50" />
            {t(`${P}.legend.skipped`)}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm border border-dashed border-amber-400 bg-amber-50" />
            {t(`${P}.legend.warning`)}
          </span>
          <span className="flex items-center gap-1">
            <EyeOff className="h-3 w-3" aria-hidden="true" />
            {t(`${P}.legend.hidden`)}
          </span>
        </div>
        {shape === 'tree' ? (
          <div className="grid gap-3">
            {objList(config.elements).map((el, i) => (
              <TreeNode key={str(el.id) || i} el={el} />
            ))}
          </div>
        ) : (
          <SectionsSketch config={config} />
        )}
      </div>
    </Ctx.Provider>
  );
}
